import { observer } from "mobx-react-lite"
import { Virtuoso } from "react-virtuoso"
import { useStores } from "../../../models/helpers/useStores"
import { useCruiseSearchResults } from "../../../services/firestore/cruiseSearchResults"
import { CruiseSuggestionCard } from "../../cards/CruiseSuggestionCard"
import styles from "./styles.module.scss"
import { DefaultSpinner } from "../../loaders/DefaultSpinner"
import ArrowsIcon from "../../assets/icons/ArrowsIcon"
import { useEffect, useMemo, useRef } from "react"
import {
  CruiseDetailsPlaceholderModel,
  CruiseSearchResult,
  cruiseSortingKeys,
  filterCruises,
  sortCruises,
} from "@cruisen/react-shared"
import { Popover } from "../../atoms/Popover"
import { SortingModal } from "./SortingModal"
import { FilteringModal } from "./FilteringModal"
import classNames from "classnames"
import SlidersIcon from "../../assets/icons/SlidersIcon"
import SaveIconOutline from "~icons/eva/save-outline"
import { CruiseListStore } from "./CruiseListStore"
import { useSaveCruisePreferences } from "./useSaveCruisePreferences"
import CruiseIcon from "../../assets/icons/CruiseIcon"
import { colors } from "@theme/colors"
import { updateTrip } from "../../../services/firestore/trips"
import { useMutation } from "@tanstack/react-query"
import { getISOString } from "../../../utils/dates"

const SavePreferencesModal = ({
  onSubmit,
  isLoading,
}: {
  onClose: () => void
  onSubmit: () => void
  isLoading: boolean
}) => {
  return (
    <div className={styles.savePreferencesModal}>
      <p>
        Would you like to save your sorting and filtering options for this trip? This will become
        the default view for all customers on this trip.
      </p>
      <div className={styles.savePreferencesModalButtons}>
        <button
          className={styles.savePreferencesModalButton}
          onClick={onSubmit}
          disabled={isLoading}
        >
          {isLoading ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  )
}

export const CruiseList = observer(function CruiseList() {
  const rootStore = useStores()
  const chosenCruiseDetails = rootStore.cruiseDetailsPlaceholderData?.id

  const { data: cruiseSearchResults, isLoading } = useCruiseSearchResults(rootStore?.activeTrip?.id)
  const { savedCruisePreferences, saveCruisePreferences } = useSaveCruisePreferences({
    tripId: rootStore?.activeTrip?.id,
    searchCriteriaHash: cruiseSearchResults?.searchCriteriaHash,
  })

  const store = useMemo(() => new CruiseListStore(), [])
  const virtuoso = useRef(null)

  const areCruisesLoaded = cruiseSearchResults.cruises.length > 0

  const filteredCruises = filterCruises(cruiseSearchResults?.cruises || [], store.selectedFilters, [
    rootStore.loggedInUser.id,
  ])

  const sortByLikesOrder = useMemo(() => {
    if (!areCruisesLoaded || !cruiseSearchResults?.searchCriteriaHash)
      return new Map<string, number>()

    return new Map(
      cruiseSearchResults?.cruises
        .sort((a, b) => {
          if (a.likes.length === b.likes.length) {
            return a.startDate.getTime() > b.startDate.getTime() ? 1 : -1
          }
          return a.likes.length > b.likes.length ? -1 : 1
        })
        .map((item, index) => [item.id, index]),
    )
  }, [areCruisesLoaded, cruiseSearchResults?.searchCriteriaHash])

  const sortedCruises = sortCruises({
    cruises: filteredCruises,
    sortBy: store.sortingOption,
    sortByLikesOrder,
  })

  const areCruisesLoading = isLoading || cruiseSearchResults.status === "isLoading"

  const { mutate: updateTripPreferences, isPending: isUpdatingTripPreferences } = useMutation({
    mutationFn: async () => {
      await updateTrip(rootStore.activeTrip.id, {
        cruiseSearchPreferences: {
          updatedAt: new Date().valueOf(),
          filters: store.selectedFilters,
          createdBy: rootStore.loggedInUser.id,
          sortingOption: store.sortingOption,
        },
      })
    },
    onSuccess: () => {
      store.setActiveModal(null)
    },
  })

  useEffect(() => {
    if (
      cruiseSearchResults.searchCriteriaHash &&
      cruiseSearchResults.searchCriteriaHash !== savedCruisePreferences?.searchCriteriaHash &&
      !rootStore.activeTrip.cruiseSearchPreferences
    ) {
      store.setSortingOption(cruiseSortingKeys.mostLikes)
      store.resetFilters()
    }
  }, [cruiseSearchResults.searchCriteriaHash])

  const isHashDefined = !!cruiseSearchResults.searchCriteriaHash

  useEffect(() => {
    const tripPrefs = rootStore.activeTrip.cruiseSearchPreferences
    const currentHash = cruiseSearchResults.searchCriteriaHash
    const savedHash = savedCruisePreferences?.searchCriteriaHash

    if (!isHashDefined) return
    if ((!tripPrefs && !savedCruisePreferences) || (currentHash !== savedHash && !tripPrefs)) return

    const preferences = [tripPrefs, savedCruisePreferences]
      .filter(Boolean)
      .sort((a, b) => (b?.updatedAt || 0) - (a?.updatedAt || 0))[0]

    store.setSortingOption(preferences?.sortingOption || cruiseSortingKeys.mostLikes)
    if (preferences?.filters) {
      store.handleFilters(preferences.filters)
    } else {
      store.resetFilters()
    }
  }, [
    rootStore.activeTrip?.cruiseSearchPreferences?.updatedAt,
    savedCruisePreferences?.updatedAt,
    isHashDefined,
  ])

  if (!rootStore.activeTrip || !rootStore?.loggedInUser) return null

  const resetCruiseListScroll = () => {
    virtuoso?.current?.scrollToIndex({ index: 0 })
  }

  const toggleCruise = (cruise: CruiseSearchResult) => {
    if (cruise.id === chosenCruiseDetails) {
      rootStore.setProp("cruiseDetailsPlaceholderData", null)
    } else {
      rootStore.setProp(
        "cruiseDetailsPlaceholderData",
        CruiseDetailsPlaceholderModel.create({
          id: cruise.id,
          name: cruise.name,
          lowestPrice: cruise.price,
          lowestPricePP: cruise.pricePP,
          startDate: getISOString(cruise.startDate),
          endDate: getISOString(cruise.endDate),
          likes: cruise.likes,
          lineLogo: cruise.lineLogoImage,
          lineName: cruise.lineName,
          imageUrl: cruise.image,
        }),
      )
    }
  }

  return (
    <div className={styles.cruiseListContainer} data-test-id="cruise-list-container">
      <div className={styles.cruiseListHeading}>
        {isLoading ? (
          <h1 className={styles.title}>Loading cruises...</h1>
        ) : (
          <div className={styles.titleContainer}>
            <div>
              <CruiseIcon color={colors.iconPrimary} width={18} height={18} />
            </div>
            <h1 className={styles.title}>{`${sortedCruises?.length} Cruises`}</h1>
          </div>
        )}
        {areCruisesLoading ? null : (
          <div className={styles.headingButtons}>
            <Popover
              isOpen={store.activeModal === "savePreferences"}
              title="Save view"
              content={
                <SavePreferencesModal
                  onClose={() => {
                    store.setActiveModal(null)
                  }}
                  onSubmit={() => updateTripPreferences()}
                  isLoading={isUpdatingTripPreferences}
                />
              }
              onClickOutside={() => {
                store.setActiveModal(null)
              }}
              align="end"
              wrapperClassName={styles.savePreferencesPopover}
            >
              <button
                className={styles.cruiseListActionButton}
                onClick={() => {
                  store.toggleActiveModal("savePreferences")
                }}
              >
                <SaveIconOutline />
              </button>
            </Popover>
            <Popover
              isOpen={store.activeModal === "sorting"}
              wrapperClassName={styles.sortingPopover}
              onClickOutside={() => store.setActiveModal(null)}
              title="Sort by"
              align="end"
              content={
                <SortingModal
                  selectedOption={store.sortingOption}
                  onClose={(option) => {
                    resetCruiseListScroll()
                    store.setSortingOption(option)
                    store.setActiveModal(null)
                    saveCruisePreferences({
                      filters: store.selectedFilters,
                      sortingOption: option,
                    })
                  }}
                />
              }
            >
              <button
                className={classNames(
                  styles.cruiseListActionButton,
                  cruiseSortingKeys.mostLikes !== store.sortingOption &&
                    styles.cruiseListActionButtonSelected,
                )}
                onClick={() => store.toggleActiveModal("sorting")}
              >
                <ArrowsIcon />
              </button>
            </Popover>
            <Popover
              isOpen={store.activeModal === "filtering"}
              wrapperClassName={styles.filteringPopover}
              onClickOutside={() => {
                if (store.isDragging) return
                if (store.isDatePickerOpen) {
                  store.setIsDatePickerOpen(false)
                  return
                }
                store.setActiveModal(null)
              }}
              align="end"
              title="Filters"
              testId="filtering-popover"
              content={
                <FilteringModal
                  currentFilters={store.selectedFilters}
                  cruises={cruiseSearchResults?.cruises || []}
                  setIsDragging={(value) => store.setIsDragging(value)}
                  setIsDatePickerOpen={(value) => store.setIsDatePickerOpen(value)}
                  updateFilters={(values) => {
                    resetCruiseListScroll()
                    store.handleFilters(values)
                    saveCruisePreferences({
                      filters: values,
                      sortingOption: store.sortingOption,
                    })
                  }}
                  onClose={() => {
                    store.setActiveModal(null)
                  }}
                />
              }
            >
              <button
                className={classNames(
                  styles.cruiseListActionButton,
                  store.selectedFilters !== null && styles.cruiseListActionButtonSelected,
                )}
                onClick={() => store.toggleActiveModal("filtering")}
                data-testid="cruise-list-filter-button"
              >
                <SlidersIcon />
              </button>
            </Popover>
          </div>
        )}
      </div>
      {areCruisesLoading ? (
        <div className={styles.loadingContainer}>
          <DefaultSpinner />
        </div>
      ) : (
        <div className={styles.cruiseListWrapper}>
          <Virtuoso
            className={styles.cruiseList}
            data={sortedCruises || []}
            ref={virtuoso}
            components={{
              Footer: () => <div className={styles.cruiseListFooter} />,
            }}
            computeItemKey={(_, cruise) => cruise.id}
            itemContent={(index, cruise) => (
              <div className={styles.cruiseListCard}>
                <CruiseSuggestionCard
                  tripId={rootStore.activeTrip.id}
                  userId={rootStore.loggedInUser.id}
                  onClick={() => toggleCruise(cruise)}
                  cruise={cruise}
                  isSelected={chosenCruiseDetails === cruise.id}
                  testId={`cruise-${index + 1}`}
                />
              </div>
            )}
          />
        </div>
      )}
    </div>
  )
})
